import Image from 'next/image';
import React from 'react';
import { Accordion, Col, Container, Row } from 'react-bootstrap';
import {
  ArrowBlue,
  ArrowRightLong,
  ArrowRightShort,
  ArrowRightWhite,
} from '../../assets/icons';
import {
  aboutUsStep3,
  medication3,
  medicationStep3,
  ratingContainer,
  weightLossStep1,
  weightLossStep2,
  starsRow,
  trustPilotNewer,
} from '../../assets/images';
import Link from 'next/link';
import Steps from '../steps';
import { TrustBoxWide } from '../trustBox';
import parser from 'html-react-parser';
import MedicationFooter from './MedicationFooter';

const MedicationBody = ({ data, title, path }) => {
  const steps = [
    {
      id: 1,
      img: weightLossStep1,
      title: `Choose ${title}`,
      body: (
        <div className='tw-flex tw-flex-col tw-gap-y-2'>
          <p className='tw-m-0 tw-text-sm tw-text-[#5F5F5F] tw-text-center lg:tw-text-left'>
            Answer a short medical questionnaire so we can understand your
            goals, health history, and whether medicine is appropriate for you.
          </p>
          <ul className='tw-text-sm tw-text-[#5F5F5F] tw-text-center tw-list-inside lg:tw-text-left lg:tw-list-outside'>
            <li>Takes 3–5 minutes</li>
            <li>No insurance required</li>
            <li>Transparent pricing before checkout</li>
          </ul>
        </div>
      ),
    },
    {
      id: 2,
      img: weightLossStep2,
      title: `Licensed Provider Review`,
      body: (
        <div className='tw-flex tw-flex-col tw-gap-y-2'>
          <p className='tw-m-0 tw-text-sm tw-text-[#5F5F5F] tw-text-center lg:tw-text-left'>
            A licensed medical provider reviews your information and determines
            if the medication is appropriate for you.
          </p>
          <ul className=' tw-text-sm tw-text-[#5F5F5F] tw-text-center tw-list-inside lg:tw-text-left lg:tw-list-outside'>
            <li>Real medical review</li>
            <li>Safe, personalized decision</li>
            <li>No commitment</li>
          </ul>
        </div>
      ),
    },
    {
      id: 3,
      img: medicationStep3,
      title: `Prescription Sent to Your Pharmacy`,
      body: (
        <div className='tw-flex tw-flex-col tw-gap-y-2'>
          <p className='tw-m-0 tw-text-sm tw-text-[#5F5F5F] tw-text-center lg:tw-text-left'>
            If approved, your prescription is electronically sent to your
            preferred pharmacy for pickup.
          </p>
          <ul className='tw-text-sm tw-text-[#5F5F5F] tw-text-center tw-list-inside lg:tw-text-left lg:tw-list-outside'>
            <li>Fast electronic delivery</li>
            <li>Secure & confidential</li>
            <li>Ongoing support if needed</li>
          </ul>
        </div>
      ),
    },
  ];

  const facts = data?.quick_facts;
  const symptoms = data?.symptoms;
  const faqs = data?.content?.faqs;
  const overview = data?.content?.overview;
  const sections = data?.content?.sections;

  return (
    <div>
      <Row className='spreadWrapper tw-mb-12'>
        {' '}
        <Col
          xs={12}
          sm={6}
          className=' tw-text-2xl lg:tw-text-[34px] tw-font-medium lg:tw-leading-[3rem]'
        >
          {' '}
          <p className=' tw-w-full lg:tw-w-1/2'>
            About this
            <span className=' gradientText'> Medication</span>
          </p>
        </Col>
        <Col xs={12} sm={6}>
          <p className=' xl:tw-text-lg tw-text-doc_gray'>
            {parser(overview || '')}
          </p>
        </Col>
      </Row>
      {sections?.length > 0 &&
        sections.map((section, index) => {
          // Headings are free text from the CMS, so the gradient goes on the
          // last word — matching "About this Medication" / "Quick Facts" above.
          const heading = (section.heading || section.title || '').trim();
          const words = heading.split(' ');
          const lead = words.slice(0, -1).join(' ');
          const last = words.slice(-1).join(' ');
          return (
            <Row className='spreadWrapper tw-mb-12' key={heading || index}>
              {' '}
              <Col
                xs={12}
                sm={6}
                className=' tw-text-2xl lg:tw-text-[34px] tw-font-medium lg:tw-leading-[3rem]'
              >
                {' '}
                <p className=' tw-w-full lg:tw-w-1/2'>
                  {lead}
                  <span className=' gradientText'>
                    {lead ? ' ' : ''}
                    {last}
                  </span>
                </p>
              </Col>
              <Col xs={12} sm={6}>
                <p className=' xl:tw-text-lg tw-text-doc_gray'>
                  {parser(section.content || '')}
                </p>
              </Col>
            </Row>
          );
        })}
      <Row className='spreadWrapper '>
        {' '}
        <Col
          xs={12}
          sm={6}
          className=' tw-text-2xl lg:tw-text-[34px] tw-font-medium lg:tw-leading-[3rem]'
        >
          {' '}
          <p className=' tw-w-full lg:tw-w-1/2'>
            Quick
            <span className=' gradientText'> Facts</span>
          </p>
        </Col>
        <Col xs={12} sm={6}>
          <div className='tw-bg-[#EAF4F7] tw-rounded-xl tw-p-6 tw-space-y-3 tw-font-medium'>
            {facts?.map((item, index) => (
              <p key={index}>
                <span className=' tw-text-black'>{item.title}:</span>{' '}
                <span className=' tw-text-doc_gray'>{item.value}</span>
              </p>
            ))}
          </div>
        </Col>
      </Row>
      <section className='tw-w-full tw-py-8 lg:tw-py-16'>
        <div className='tw-mx-auto tw-w-[92%] lg:tw-w-[83%] 2xl:tw-w-[90rem]'>
          <Container fluid className='px-0'>
            <Row>
              <Col lg={5}>
                <div className='tw-w-full tw-flex tw-flex-col tw-gap-y-8  lg:tw-gap-y-10'>
                  <h2 className='tw-m-0 tw-text-2xl lg:tw-text-4xl XL:tw-text-[2.5rem] tw-font-semibold !tw-leading-[3.5rem]'>
                    Is {title}{' '}
                    <span className='gradientText'>Right For You?</span>
                  </h2>
                  <div className='tw-w-full tw-flex tw-flex-col tw-gap-y-6'>
                    <p className='tw-m-0 tw-text-[#5F5F5F] tw-text-sm lg:tw-text-base'>
                      You may be a candidate if you:
                    </p>
                    <ul className='tw-p-0 tw-list-none'>
                      {symptoms?.map((symptom, index) => (
                        <li
                          key={index}
                          style={{
                            borderBottom: '0.5px solid rgba(0,0,0,.125)',
                          }}
                          className='tw-py-3 tw-flex tw-items-center tw-gap-x-2 tw-text-sm'
                        >
                          <div className='tw-flex-shrink-0'>
                            {' '}
                            <ArrowBlue />
                          </div>

                          <span className='tw-m-0 tw-text-[#5F5F5F] tw-text-sm '>
                            {symptom}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className='tw-m-0 tw-text-[#5F5F5F]'>
                      {/* A licensed provider will review your information to
                      determine whether {title} is appropriate for your
                      situation. */}
                      Not sure? A provider will review your information before
                      prescribing to ensure it’s safe and appropriate.
                    </p>
                    <button
                      //onClick={() => getTreated('EPzuICJy88')}
                      className='refill-cta tw-border-none tw-w-full lg:tw-w-max tw-mt-4 '
                    >
                      <Link href={`${path}`}>
                        <div className='tw-w-full tw-bg-black tw-text-white tw-flex tw-items-center tw-gap-x-2 tw-justify-center tw-px-4 tw-py-3 tw-rounded-full tw-font-medium tw-text-base'>
                           Begin Medical Assessment 
                          <ArrowRightShort />
                        </div>
                      </Link>
                    </button>
                  </div>
                </div>
              </Col>
              <Col
                lg={{ span: 6, offset: 1 }}
                className='tw-relative tw-mt-6 lg:tw-mt-0'
              >
                <div className='position-relative tw-flex tw-flex-col tw-w-full tw-h-[343px] lg:tw-h-full '>
                  <Image
                    src={medication3}
                    alt='weight loss benefits'
                    layout='fill'
                    //objectFit='cover'
                    className='tw-rounded-2xl tw-object-cover'
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </section>
      <Steps
        superText={'Simple 3-Step Process'}
        subText=<div>
          Get your prescription reviewed and sent — without the wait.
          <br className='tw-hidden lg:tw-block' />
          If approved, your prescription is sent to your pharmacy for pickup
          often within hours.
        </div>
        steps={steps}
        buttonText={'Begin Medical Assessment '}
        showTicker={false}
        path={path}
      />
      {/**Tentative patient reviews */}
      <section className='tw-w-full tw-py-8 lg:tw-py-16'>
        <div className='tw-mx-auto tw-w-[92%] lg:tw-w-[83%] 2xl:tw-w-[90rem]'>
          <div className='tw-w-full tw-flex tw-flex-col tw-gap-y-4'>
            <p className='tw-m-0 tw-font-medium tw-text-[#195D9D] lg:tw-text-xl'>
              Patient Reviews
            </p>
            <div className='tw-w-full tw-flex tw-flex-col tw-gap-y-4 lg:tw-flex-row lg:tw-items-center'>
              <div className='tw-flex tw-flex-col tw-gap-y-4 lg:tw-flex-1'>
                <p className='tw-m-0 tw-text-2xl xl:tw-text-4xl tw-font-semibold'>
                  Trusted by 12,000+ patients nationwide.
                </p>
                <h2 className='tw-m-0 tw-font-semibold tw-text-[2rem] xl:tw-text-5xl'>
                  Real relief. Real prescriptions.
                  <span className='gradientText'>Real fast.</span>
                </h2>
                <p className='tw-m-0 tw-text-[#5f5f5f]'>
                  Over 80% of patients complete treatment in under 15 minutes.
                </p>
              </div>
              <div className=' tw-flex tw-flex-col tw-items-center lg:tw-items-start tw-gap-y-2'>
                <p className='tw-m-0 tw-text-[2.5rem] tw-font-semibold'>4.2</p>
                <div className=' tw-flex tw-flex-col tw-items-center lg:tw-items-start tw-gap-y-1'>
                  <Image
                    src={starsRow}
                    width={120}
                    height={24}
                    alt='Four Stars'
                  />
                  <span className='tw-m-0 tw-text-[#5f5f5f]'>
                    Based on 13,000+
                  </span>
                </div>
                <Image
                  src={trustPilotNewer}
                  width={140}
                  height={30}
                  alt='Trustpilot'
                />
              </div>
            </div>
          </div>
          <div className='tw-w-full tw-py-8 lg:tw-py-16'>
            {' '}
            <TrustBoxWide />
          </div>
        </div>
      </section>

      {/*Tentative faqs*/}
      {faqs !== undefined && faqs.length > 0 && (
        <section className='tw-w-full tw-py-8 lg:tw-py-16'>
          <div className='tw-mx-auto tw-w-[92%] lg:tw-w-[83%] 2xl:tw-w-[90rem]'>
            <Container fluid className='px-0'>
              <Row>
                <Col xs={12} lg={5}>
                  <h2 className=' tw-text-[36px] lg:tw-text-[40px] tw-font-semibold'>
                    Have questions?
                    <br />
                    <span className='gradientText'> Get answers</span>
                  </h2>
                  <p className=' tw-text-doc_gray  tw-mt-6'>
                    *24hrdoc is not a pharmacy. We do not carry or
                    <br className='tw-hidden lg:tw-block' /> ship medicines.
                    Online Doctor Prescriptions are{' '}
                    <br className='tw-hidden lg:tw-block' /> sent electronically
                    to your pharmacy after an{' '}
                    <br className='tw-hidden lg:tw-block' /> online medical
                    consultation.
                  </p>
                </Col>
                <Col xs={12} lg={7}>
                  <Accordion flush className='theAccordion'>
                    {faqs.map((item, index) => (
                      <Accordion.Item key={index} eventKey={index}>
                        <Accordion.Header>{item.title}</Accordion.Header>
                        <Accordion.Body>{item.content}</Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Col>
              </Row>
            </Container>
          </div>
        </section>
      )}

      <MedicationFooter path={path} />
    </div>
  );
};

export default MedicationBody;
